import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useRouter } from '@tanstack/react-router'
import { toast } from 'sonner'
import { resetBypass } from '~/app/admin-bypass'
import { adminApi } from '~/entities/session/api/admin'
import { authApi } from '~/entities/session/api/auth'
import { useSupabaseSession } from '~/entities/session/model/use-auth'
import { useRoleAuth } from '~/entities/session/model/use-role-auth'
import * as m from '~/paraglide/messages'
import { getErrorMessageFromError } from '~/shared/lib/api-errors'
import { log } from '~/shared/lib/logger'
import { CacheScope, clearScope, QueryKey, queryKey } from '~/shared/lib/query-keys'
import { storageManager } from '~/shared/lib/storage-manager'
import type { BusinessListItem, InviteBusinessInput } from '~/shared/types/admin'

export function useAdminBusinesses() {
  const session = useSupabaseSession()
  return useQuery({
    queryKey: queryKey(QueryKey.ADMIN_BUSINESSES),
    queryFn: () => adminApi.getAllBusinesses(),
    enabled: !!session && storageManager.isAdmin(),
    staleTime: 2 * 60 * 1000,
  })
}

export function useInviteBusiness() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: InviteBusinessInput) => authApi.inviteBusinessOwner(data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: queryKey(QueryKey.ADMIN_BUSINESSES),
      })
      if (!data.notificationSent) {
        toast.warning(m.businessCreatedButEmailFailed(), {
          description: m.businessCreatedButEmailFailedDescription(),
        })
      } else if (data.existingAccount) {
        toast.success(m.businessAddedToExistingAccount())
      } else {
        toast.success(m.businessCreatedSuccessfully())
      }
    },
    onError: (error: unknown) => {
      log.error(error as Error, '[Invite Business] Failed')
      toast.error(getErrorMessageFromError(error))
    },
  })
}

export function useAdminViewBusiness() {
  const router = useRouter()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (business: BusinessListItem) => {
      queryClient.cancelQueries()
      clearScope(queryClient, CacheScope.BUSINESS)
      resetBypass()

      storageManager.setAdminViewingBusinessId(business.id)
      storageManager.setAdminViewingBusinessData({
        id: business.id,
        legalName: business.legalName,
      })
      return business
    },
    onSuccess: (business) => {
      toast.success(m.nowViewingBusiness({ businessName: business.legalName }))
      router.navigate({ to: '/dashboard' })
    },
    onError: (error: unknown) => {
      log.error(error as Error, '[Admin View Business] Failed')
      toast.error(getErrorMessageFromError(error))
    },
  })
}

export function useAdminExitBusiness() {
  const router = useRouter()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      queryClient.cancelQueries()
      clearScope(queryClient, CacheScope.BUSINESS)
      resetBypass()
      storageManager.clearAdminViewingContext()
      return Promise.resolve()
    },
    onSuccess: () => {
      toast.success(m.exitedBusinessView())
      router.navigate({ to: '/admin' })
    },
  })
}

export function useAdminAuth() {
  const {
    data: admin,
    isLoading,
    isAuthenticated,
    error,
  } = useRoleAuth({
    queryKey: queryKey(QueryKey.ADMIN_CURRENT_USER),
    queryFn: () => adminApi.getCurrentAdmin(),
    enabled: (session) => !!session && storageManager.isAdmin(),
  })

  return {
    admin,
    isAdmin: isAuthenticated,
    isAuthenticated,
    isLoading,
    error,
  }
}
